import React, {useCallback, useMemo} from 'react';
import {useController, useFormContext, useWatch} from "react-hook-form";
import AutocompleteSelect from "v4/components/form/Field/components/AutocompleteSelect/AutocompleteSelect";
import CustomReactSelect from "v4/components/form/Field/components/CustomReactSelect/CustomReactSelect";
import DisplayFields from "v4/components/form/Field/components/DisplayFields/DisplayFields";
import InputCheckboxes from "v4/components/form/Field/components/InputCheckboxes/InputCheckboxes";
import InputCompare from "v4/components/form/Field/components/InputCompare/InputCompare";
import InputDate from "v4/components/form/Field/components/InputDatetime/InputDate";
import InputEmail from "v4/components/form/Field/components/InputEmail/InputEmail";
import InputFile from "v4/components/form/Field/components/InputFile/InputFile";
import InputNumber from "v4/components/form/Field/components/InputNumber/InputNumber";
import InputPostalCode from "v4/components/form/Field/components/InputPostalCode/InputPostalCode";
import InputRadio from "v4/components/form/Field/components/InputRadio/InputRadio";
import InputRange from "v4/components/form/Field/components/InputRange/InputRange";
import InputRichText from "v4/components/form/Field/components/InputRichText/InputRichText";
import InputSimpleCheckbox from "v4/components/form/Field/components/InputSimpleCheckbox/InputSimpleCheckbox";
import InputTel from "v4/components/form/Field/components/InputTel/InputTel";
import InputText from "v4/components/form/Field/components/InputText/InputText";
import InputSiret from "v4/components/form/Field/components/InputSiret/InputSiret";
import CollectionTypeField from "v4/components/form/Field/components/CollectionTypeField/CollectionTypeField";
import QuoteLines from "v4/components/form/Field/components/QuoteLines/QuotesLines";
import useConditionalField from "v4/components/form/Field/hooks/useConditionalField";
import useDependsOnField from "v4/components/form/Field/hooks/useDependsOnField";
import {usePolTranslation} from "v4/hooks/usePolTranslation";
import InputPassword from "v4/components/form/Field/components/InputPassword/InputPassword";
import 'v4/components/form/Field/Field.scss';

import {
    CHECKBOX_TYPE,
    CHOICE_TYPE,
    COLLECTION_TYPE,
    CUSTOMER_FILE_TYPE,
    DATE_COMPARE_TYPE,
    DATE_TIME_TYPE,
    DATE_TYPE,
    EMAIL_TYPE,
    FILE_TYPE,
    HIDDEN_TYPE,
    LIVE_COLLECTION_TYPE,
    NUMBER_COMPARE_TYPE,
    NUMBER_TYPE,
    PASSWORD_TYPE,
    QUOTE_LINE_TYPE,
    TEL_TYPE,
    TEXT_TYPE,
    TEXTAREA_TYPE,
    URL_TYPE
} from "v4/data/fieldTypes";
import getFieldConstraints from "v4/utils/getFieldConstraints";
import clearCollectionTypeValue from "v4/utils/clearCollectionTypeValue";
import InputUrl from "v4/components/form/Field/components/InputUrl/InputUrl";
import useUniqueOptionsWith from "v4/components/form/Field/hooks/useUniqueOptionsWith";

export default React.forwardRef(function Field({
                                                   type,
                                                   name,
                                                   label,
                                                   value: defaultValue,
                                                   choices,
                                                   multiple,
                                                   expanded,
                                                   properties,
                                                   constraints,
                                                   attr,
                                                   placeholder = '',
                                                   tip,
                                                   onFieldChange,
                                                   specificParams = {},
                                                   prefix = '',
                                                   entry_type,
                                                   entityId,
                                                   button_add_options,
                                                   button_remove_options,
                                                   impossibleValues = [],
                                                   formMetadata,
                                                   scale = null,
                                               }, ref) {
    const {t} = usePolTranslation();
    const {control, getFieldState, formState} = useFormContext();
    const {error} = getFieldState(name, formState);

    // Fix readOnly attribute
    if ('readonly' in (attr ?? {})) {
        attr.readOnly = attr.readonly;
        delete attr.readonly;
    }

    const {
        'data-condition-field': conditionField,
        'data-condition-value': conditionFieldValues,
        'data-postal-code-target': postalCodeTarget,
        'data-wysiwyg-mode': richTextEditorMode,
        'display-fields': displayFields,
        'data-autocomplete-get-params': autocompleteGetParams,
        'unique-options-with': uniqueOptionsWith,
        'use-scale': useScale,
        dependsOnField,
        type: attrType,
        filterKey,
        ...cleanedAttr
    } = attr ?? {};

    const hideField = useConditionalField(conditionField, conditionFieldValues);

    const {
        dependsOnFieldChoices,
        autocompleteAdditionnalSearchFilter
    } = useDependsOnField(name, dependsOnField, choices);

    const {
        finalChoices,
    } = useUniqueOptionsWith(name, uniqueOptionsWith, dependsOnFieldChoices);

    const constraintsName = Object.values(constraints ?? {}).map(({propertyPath}) => propertyPath).filter(Boolean);
    const values = useWatch({control, disabled: (!constraintsName || constraintsName.length === 0)}) ?? [];
        // scale = 5 -> step = 0.00001 (uniquement si attr['use-scale'] est actif)
    const step = useMemo(() => (useScale && scale !== null ? Math.pow(10, -scale) : null), [useScale, scale]);
    const inputConstraints = useMemo(() => getFieldConstraints(constraints, type, values, step, t), [constraints, type, values, step, t]);

    if (attrType === 'hidden') type = 'hidden';

    const {
        field: {
            onChange,
            name: fieldName,
            value: fieldValue,
        },
        fieldState,
    } = useController({
        control: control,
        name: name,
        rules: inputConstraints,
        defaultValue: getDefaultValue({defaultValue, type, entry_type, properties, multiple, choices}),
        disabled: hideField,
    })

    const onChangeMemoized = useCallback((value) => {
        onChange(value)
        onFieldChange && onFieldChange(value, {name: fieldName})
    }, [onChange, onFieldChange, fieldName]);

    if (hideField) return null;

    switch (type) {
        case 'text':
        case TEXT_TYPE:
            if (postalCodeTarget) {
                return <>
                    {tip && <p className="inpu-field__tip">{tip}</p>}
                    {error && <p className="input-field__errors">{error.message}</p>}

                    <InputPostalCode fieldName={fieldName}
                                     fieldValue={fieldValue}
                                     inputConstraints={inputConstraints}
                                     placeholder={placeholder}
                                     prefix={prefix}
                                     attr={cleanedAttr}
                                     error={error}
                                     postalCodeTarget={postalCodeTarget}
                                     onChange={onChangeMemoized}/>
                </>
            }

            if (attrType === 'siret') {
                return <>
                    {tip && <p className="inpu-field__tip">{tip}</p>}
                    {error && <p className="input-field__errors">{error.message}</p>}

                    <InputSiret fieldName={fieldName}
                                fieldValue={fieldValue}
                                inputConstraints={inputConstraints}
                                placeholder={placeholder}
                                prefix={prefix}
                                attr={cleanedAttr}
                                error={error}
                                onChange={onChangeMemoized}/>
                </>
            }

            return <>
                {tip && <p className="inpu-field__tip">{tip}</p>}
                {error && <p className="input-field__errors">{error.message}</p>}

                <InputText fieldName={fieldName}
                           fieldValue={fieldValue}
                           inputConstraints={inputConstraints}
                           placeholder={placeholder}
                           prefix={prefix}
                           attr={cleanedAttr}
                           error={error}
                           onChange={onChangeMemoized}/>
            </>
        case 'number':
        case NUMBER_TYPE:
            if (attrType === 'range' || attrType === 'slider') {
                return <>
                    {tip && <p className="inpu-field__tip">{tip}</p>}
                    {error && <p className="input-field__errors">{error.message}</p>}

                    <InputRange fieldValue={fieldValue}
                                fieldName={fieldName}
                                attributes={cleanedAttr}
                                htmlConstraints={inputConstraints}
                                onChange={onChangeMemoized}/>
                </>
            }

            return <>
                {tip && <p className="inpu-field__tip">{tip}</p>}
                {error && <p className="input-field__errors">{error.message}</p>}

                <InputNumber fieldName={fieldName}
                             fieldValue={fieldValue}
                             attr={{ step, ...cleanedAttr }}
                             inputConstraints={inputConstraints}
                             placeholder={placeholder}
                             prefix={prefix}
                             error={error}
                             onChange={onChangeMemoized}/>
            </>
        case PASSWORD_TYPE:
            return <>
                {tip && <p className="inpu-field__tip">{tip}</p>}
                {error && <p className="input-field__errors">{error.message}</p>}

                <InputPassword fieldName={fieldName}
                               fieldValue={fieldValue}
                               inputConstraints={inputConstraints}
                               placeholder={placeholder}
                               prefix={prefix}
                               attr={cleanedAttr}
                               error={error}
                               onChange={onChangeMemoized}/>
            </>
        case TEL_TYPE:
            return <>
                {tip && <p className="inpu-field__tip">{tip}</p>}
                {error && <p className="input-field__errors">{error.message}</p>}

                <InputTel fieldName={fieldName}
                          inputConstraints={inputConstraints}
                          placeholder={placeholder}
                          prefix={prefix}
                          attr={cleanedAttr}
                          error={error}
                          fieldValue={fieldValue}
                          onChange={onChangeMemoized}/>
            </>
        case 'email':
        case EMAIL_TYPE:
            return <>
                {tip && <p className="inpu-field__tip">{tip}</p>}
                {error && <p className="input-field__errors">{error.message}</p>}

                <InputEmail fieldName={fieldName}
                            fieldValue={fieldValue}
                            inputConstraints={inputConstraints}
                            placeholder={placeholder}
                            prefix={prefix}
                            attr={cleanedAttr}
                            error={error}
                            onChange={onChangeMemoized}/>
            </>
        case URL_TYPE:
            return <>
                {tip && <p className="inpu-field__tip">{tip}</p>}
                {error && <p className="input-field__errors">{error.message}</p>}

                <InputUrl fieldName={fieldName}
                          fieldValue={fieldValue}
                          inputConstraints={inputConstraints}
                          placeholder={placeholder}
                          prefix={prefix}
                          attr={cleanedAttr}
                          error={error}
                          onChange={onChangeMemoized}/>
            </>
        case 'hidden':
        case HIDDEN_TYPE:
            return <>
                <input className={`input-field${error ? ' has-error' : ''}`}
                       type="hidden"
                       id={prefix + fieldName}
                       defaultValue={fieldValue}
                       {...cleanedAttr}/>
            </>
        case 'datetime':
        case DATE_TIME_TYPE:
            return <>
                {tip && <p className="inpu-field__tip">{tip}</p>}
                {error && <p className="input-field__errors">{error.message}</p>}

                <InputDate fieldName={fieldName}
                           fieldValue={fieldValue}
                           withTime={true}
                           inputConstraints={inputConstraints}
                           placeholder={placeholder}
                           prefix={prefix}
                           attr={cleanedAttr}
                           error={error}
                           onChange={onChangeMemoized}/>
            </>
        case 'date':
        case DATE_TYPE:
            return <>
                {tip && <p className="inpu-field__tip">{tip}</p>}
                {error && <p className="input-field__errors">{error.message}</p>}

                <InputDate fieldName={fieldName}
                           fieldValue={fieldValue}
                           inputConstraints={inputConstraints}
                           placeholder={placeholder}
                           prefix={prefix}
                           attr={cleanedAttr}
                           error={error}
                           onChange={onChangeMemoized}/>
            </>
        case 'textarea':
        case TEXTAREA_TYPE:
            if (cleanedAttr?.['rich-text-editor']) {
                return <>
                    {tip && <p className="inpu-field__tip">{tip}</p>}
                    {error && <p className="input-field__errors">{error.message}</p>}

                    <InputRichText fieldValue={fieldValue}
                                   fieldName={fieldName}
                                   inputConstraints={inputConstraints}
                                   isRestricted={richTextEditorMode === 'restricted'}
                                   {...cleanedAttr}
                                   ref={ref}
                                   overrideTinyMceProps={specificParams}
                                   formMetadata={formMetadata}
                                   onChange={onChangeMemoized}/>
                </>
            }

            return <>
                    {tip && <p className="inpu-field__tip">{tip}</p>}
                {error && <p className="input-field__errors">{error.message}</p>}

                <textarea className={`input-field${error ? ' has-error' : ''}`}
                          id={prefix + fieldName}
                          value={fieldValue}
                          placeholder={placeholder}
                          {...cleanedAttr}
                          data-is-required={!!inputConstraints?.required}
                          onChange={(e) => {
                              onChangeMemoized(e.currentTarget.value)
                          }}/>
            </>
        case 'checkbox':
        case CHECKBOX_TYPE:
            return <>
                {tip && <p className="inpu-field__tip">{tip}</p>}
                {error && <p className="input-field__errors">{error.message}</p>}

                <InputSimpleCheckbox fieldValue={fieldValue}
                                     fieldName={fieldName}
                                     inputConstraints={inputConstraints}
                                     prefix={prefix}
                                     attr={cleanedAttr}
                                     error={error}
                                     onChange={onChangeMemoized}/>
            </>
        case CUSTOMER_FILE_TYPE:
        case FILE_TYPE:
            return <>
                {tip && <p className="inpu-field__tip">{tip}</p>}
                {error && <p className="input-field__errors">{error.message}</p>}

                <InputFile fieldValue={fieldValue}
                           fieldName={fieldName}
                           multiple={false}
                           type={type}
                           properties={properties}
                           htmlConstraints={inputConstraints}
                           fileConstraints={constraints?.File}
                           ref={ref}
                           onChange={onChangeMemoized}/>
            </>
        case COLLECTION_TYPE:
        case LIVE_COLLECTION_TYPE:
            if (entry_type === CUSTOMER_FILE_TYPE) {
                return <>
                    {tip && <p className="inpu-field__tip">{tip}</p>}
                    {error && <p className="input-field__errors">{error.message}</p>}

                    <InputFile fieldValue={fieldValue}
                               ref={ref}
                               fieldName={fieldName}
                               multiple={true}
                               type={entry_type}
                               properties={properties}
                               htmlConstraints={inputConstraints}
                               onChange={onChangeMemoized}/>
                </>
            }

            if (entry_type === QUOTE_LINE_TYPE) {
                return <>
                    {tip && <p className="inpu-field__tip">{tip}</p>}
                    {error && <p className="input-field__errors">{error.message}</p>}

                    <QuoteLines properties={properties} onChange={onChangeMemoized}/>
                </>
            }

            return (
                <>
                    {tip && <p className="inpu-field__tip">{tip}</p>}
                    {error && <p className="input-field__errors">{error.message}</p>}

                    <CollectionTypeField label={label}
                                         name={name}
                                         value={fieldValue}
                                         properties={properties}
                                         hasError={!!fieldState.error}
                                         collectionTypeConstraints={inputConstraints.collectionType}
                                         buttonAddOptions={button_add_options}
                                         buttonRemoveOptions={button_remove_options}
                    />
                </>

            )

        case 'select':
        case CHOICE_TYPE:
            if (displayFields) {
                return <DisplayFields label={label} choices={choices} attr={cleanedAttr}/>
            }

            const {'is-expanded': isExpanded} = cleanedAttr ?? {};
            if ((isExpanded || expanded) && !multiple) {
                return <>
                    {tip && <p className="inpu-field__tip">{tip}</p>}
                    {error && <p className="input-field__errors">{error.message}</p>}

                    <InputRadio fieldName={fieldName} fieldValue={fieldValue}
                                choices={choices} prefix={prefix}
                                htmlConstraints={inputConstraints}
                                onChange={onChangeMemoized}/>
                </>
            }
            if ((isExpanded || expanded) && multiple) {
                return <>
                    {tip && <p className="inpu-field__tip">{tip}</p>}
                    {error && <p className="input-field__errors">{error.message}</p>}

                    <InputCheckboxes fieldName={fieldName}
                                     fieldValue={fieldValue}
                                     choices={choices} prefix={prefix}
                                     htmlConstraints={inputConstraints}
                                     onChange={onChangeMemoized}/>
                </>
            }

            if (attrType === 'autocomplete') {
                const {'autocomplete_entity': entity} = cleanedAttr;

                return <>
                    <p className="inpu-field__tip">{tip ?? t('need_more_letter_to_search', {nb_letters: 3})}</p>
                    {error && <p className="input-field__errors">{error.message}</p>}

                    <AutocompleteSelect name={fieldName}
                                        value={fieldValue}
                                        options={finalChoices}
                                        entity={entity}
                                        autocompleteAdditionnalSearchFilter={autocompleteAdditionnalSearchFilter}
                                        autocompleteGetParams={autocompleteGetParams}
                                        isMulti={multiple}
                                        selectParams={specificParams}
                                        placeholder={placeholder ? placeholder : `${t('search')}...`}
                                        htmlConstraints={inputConstraints}
                                        impossibleValues={impossibleValues}
                                        isReadOnly={attr?.readOnly}
                                        onChange={onChangeMemoized}/>
                </>
            }
            return <>
                {tip && <p className="inpu-field__tip">{tip}</p>}
                {error && <p className="input-field__errors">{error.message}</p>}

                <CustomReactSelect name={fieldName} value={fieldValue}
                                   options={finalChoices} isMulti={multiple}
                                   selectParams={specificParams} placeholder={placeholder}
                                   htmlConstraints={inputConstraints} isReadOnly={attr?.readOnly}
                                   onChange={onChangeMemoized}/>
            </>
        case NUMBER_COMPARE_TYPE:
            return <>
                {tip && <p className="inpu-field__tip">{tip}</p>}
                {error && <p className="input-field__errors">{error.message}</p>}

                <InputCompare type="number"
                              fieldValue={fieldValue}
                              htmlConstraints={inputConstraints}
                              onChange={onChangeMemoized}/>
            </>
        case DATE_COMPARE_TYPE:
            return <>
                {tip && <p className="inpu-field__tip">{tip}</p>}
                {error && <p className="input-field__errors">{error.message}</p>}

                <InputCompare type="date"
                              fieldValue={fieldValue}
                              htmlConstraints={inputConstraints}
                              onChange={onChangeMemoized}/>
            </>
        default:
            return <p className="input-field__error">L'input de type {type ?? '"erreur"'} n'est pas géré</p>;
    }
})

function getDefaultValue({defaultValue, type, entry_type, properties, multiple, choices}) {
    switch (type) {
        case COLLECTION_TYPE:
        case LIVE_COLLECTION_TYPE:
            if (entry_type !== CUSTOMER_FILE_TYPE && entry_type !== QUOTE_LINE_TYPE) {
                return clearCollectionTypeValue(defaultValue, properties) ?? [];
            }
            return defaultValue ?? [];

        case CHOICE_TYPE: {
            const emptyValue = multiple ? [] : '';

            if (!choices?.length || !choices.some(({value}) => [defaultValue].flat().includes(value))) {
                return emptyValue;
            }

            return defaultValue ?? emptyValue;
        }
        case NUMBER_COMPARE_TYPE:
        case DATE_COMPARE_TYPE:
            return defaultValue ?? {equal: '=', begin: "", end: ""};

        default:
            return defaultValue ?? "";
    }
}
